import { Component } from '@theme/component';

/**
 * One real Shopify Forms instance, scoped to this component. Flow owns issuance.
 * @extends {Component<{launcher: HTMLAnchorElement, panel: HTMLDivElement, close: HTMLButtonElement, formBody: HTMLDivElement, loading: HTMLParagraphElement, fallback: HTMLAnchorElement}>}
 */
class MaxDecalWelcomeCash extends Component {
  #abort;
  #timer;
  #elapsed = 0;
  #last = 0;
  #seen = false;
  #hoverOpened = false;
  #key = 'maxdecal-welcome-cash-shown-v1';

  connectedCallback() {
    super.connectedCallback();
    if (this.dataset.designMode !== 'true' && this.parentElement !== document.body) {
      queueMicrotask(() => { if (this.isConnected) document.body.append(this); });
      return;
    }
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    this.#cleanup();
    super.disconnectedCallback();
  }

  #cleanup() {
    this.#abort?.abort();
    clearInterval(this.#timer);
  }

  #initialize() {
    this.#cleanup();
    if (!this.refs.panel || !this.refs.launcher) return;
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    try { this.#seen = sessionStorage.getItem(this.#key) === '1'; } catch { /* Storage may be disabled. */ }
    this.#last = Date.now();
    document.addEventListener('visibilitychange', () => { this.#last = Date.now(); }, { signal });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.refs.panel.hidden) this.closePanel();
    }, { signal });
    this.refs.launcher.addEventListener('keydown', (event) => {
      if (event.key === ' ') { event.preventDefault(); this.togglePanel(event); }
    }, { signal });
    document.addEventListener('shopify:section:unload', (event) => {
      if (event.detail?.sectionId === this.dataset.sectionId) this.remove();
    }, { signal });
    this.#timer = setInterval(() => this.#tick(), 500);
    this.#tick();
  }

  #chatOpen() {
    // Verified MaxDecal Shopify Product Advisor DOM; no customer data is read.
    return Boolean(document.querySelector('shopify-chat')?.shadowRoot
      ?.querySelector('shopify-agent-runtime')?.hasAttribute('open'));
  }

  #remember() {
    this.#seen = true;
    try { sessionStorage.setItem(this.#key, '1'); } catch { /* Retain in-memory state. */ }
  }

  #tick() {
    const now = Date.now();
    if (document.visibilityState === 'visible') this.#elapsed += now - this.#last;
    this.#last = now;
    const formRoot = this.refs.formBody.querySelector('[data-forms-id="forms-root-1184635"]');
    const ready = Boolean(formRoot?.querySelector('shopify-forms-embed')?.shadowRoot?.querySelector('form'));
    this.refs.loading.hidden = ready;
    this.refs.fallback.hidden = ready;
    const chatOpen = this.#chatOpen();
    if (chatOpen && !this.refs.panel.hidden) this.#hide(false);
    if (!this.#seen && this.dataset.autoHome === 'true' && this.dataset.designMode !== 'true'
      && this.#elapsed >= 15000 && document.visibilityState === 'visible' && !chatOpen) {
      this.#show(false);
    }
  }

  #show(focus) {
    if (this.#chatOpen()) return;
    this.refs.panel.hidden = false;
    this.refs.launcher.setAttribute('aria-expanded', 'true');
    this.#remember();
    if (focus) this.refs.close.focus({ preventScroll: true });
  }

  #hide(focus) {
    this.#hoverOpened = false;
    this.refs.panel.hidden = true;
    this.refs.launcher.setAttribute('aria-expanded', 'false');
    if (focus) this.refs.launcher.focus({ preventScroll: true });
  }

  /** @param {Event} event */
  togglePanel(event) {
    event.preventDefault();
    if (this.#hoverOpened && !this.refs.panel.hidden) {
      this.#hoverOpened = false;
      this.refs.close.focus({ preventScroll: true });
      return;
    }
    if (this.refs.panel.hidden) this.#show(true);
    else this.closePanel();
  }

  /** @param {PointerEvent} event */
  hoverOpen(event) {
    if (this.refs.panel.hidden && event.pointerType === 'mouse' && matchMedia('(hover: hover)').matches) {
      this.#hoverOpened = true;
      this.#show(false);
    }
  }

  closePanel() {
    this.#remember();
    this.#hide(true);
  }
}

if (!customElements.get('md-welcome-cash-component')) {
  customElements.define('md-welcome-cash-component', MaxDecalWelcomeCash);
}
